import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Shared presentational pieces. Every status carries an icon glyph and a word so
 * it survives greyscale printing and colour-blindness (§7).
 */

export function Badge({
  tone = 'muted',
  icon,
  children,
}: {
  tone?: 'ok' | 'warn' | 'danger' | 'info' | 'muted' | string;
  icon?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <span className={`badge ${tone}`}>
      {icon ? <span aria-hidden="true">{icon}</span> : null}
      {children}
    </span>
  );
}

export function Loading({ label = 'جارٍ التحميل…' }: { label?: string }): JSX.Element {
  return (
    <div className="loading" role="status" aria-live="polite">
      {label}
    </div>
  );
}

export function Empty({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      {hint ? <div className="empty-hint">{hint}</div> : null}
      {action ? <div className="empty-action">{action}</div> : null}
    </div>
  );
}

export function Failure({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}): JSX.Element {
  const message = error instanceof Error ? error.message : 'حدث خطأ غير متوقع';
  const requestId = (error as { requestId?: string })?.requestId;
  return (
    <div className="failure" role="alert">
      <div style={{ fontWeight: 600 }}>تعذر إتمام العملية</div>
      <div style={{ marginTop: 6 }}>{message}</div>
      {requestId ? (
        <div style={{ marginTop: 6, fontSize: 12.5 }}>
          معرّف الطلب: <span className="mono">{requestId}</span>
        </div>
      ) : null}
      {onRetry ? (
        <div style={{ marginTop: 12 }}>
          <button onClick={onRetry}>إعادة المحاولة</button>
        </div>
      ) : null}
    </div>
  );
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'danger';
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={`notice ${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      {children}
    </div>
  );
}

export function Card({
  title,
  subtitle,
  actions,
  children,
}: {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="card">
      {title ? (
        <div className="row between" style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>
            {title}
            {subtitle ? <span className="sub">{subtitle}</span> : null}
          </h2>
          {actions ? <div className="row">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function Stat({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: ReactNode;
  tone?: 'ok' | 'warn' | 'danger';
  hint?: string;
}): JSX.Element {
  return (
    /* The tone is on the tile as well as the number: the edge hairline survives
       greyscale printing, where a coloured figure alone would not (§7). */
    <div className={`stat ${tone ? `tone-${tone}` : ''}`}>
      <div className="label">{label}</div>
      <div className={`value ${tone ?? ''}`}>{value}</div>
      {hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <h3>{title}</h3>
        {children}
        {footer ? (
          <div className="row end" style={{ marginTop: 16 }}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint ? <div className="hint">{hint}</div> : null}
      {error ? <div className="error">{error}</div> : null}
    </div>
  );
}

export function Pagination({
  page,
  totalPages,
  total,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (page: number) => void;
}): JSX.Element | null {
  if (totalPages <= 1) {
    return (
      <div className="row end" style={{ marginTop: 10, color: 'var(--muted)', fontSize: 13 }}>
        <span>{total} عنصرًا</span>
      </div>
    );
  }
  return (
    <div className="row between" style={{ marginTop: 12 }}>
      <span style={{ color: 'var(--muted)', fontSize: 13 }}>
        {total} عنصرًا — صفحة <span className="num">{page}</span> من{' '}
        <span className="num">{totalPages}</span>
      </span>
      <div className="row">
        <button disabled={page <= 1} onClick={() => onChange(page - 1)}>
          السابق
        </button>
        <button disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
          التالي
        </button>
      </div>
    </div>
  );
}

/** Confirmation for anything that makes real sound in a real school (§7). */
export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'تأكيد',
  danger,
  busy,
  onConfirm,
  onCancel,
  children,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}): JSX.Element {
  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button onClick={onCancel} disabled={busy}>
            إلغاء
          </button>
          <button className={danger ? 'danger' : 'primary'} onClick={onConfirm} disabled={busy}>
            {busy ? 'جارٍ التنفيذ…' : confirmLabel}
          </button>
        </>
      }
    >
      <div>{message}</div>
      {children}
    </Modal>
  );
}
